import React, { useEffect, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { disputeReviewAPI } from '../services/api'
import Panel from './Panel'

const FAULT_LABELS = {
  model: "Model didn't follow its prompt",
  prompt: 'Prompt gap',
  data_or_tool: 'Tool or data problem',
}

// Desktop height of the problems row; long lists scroll inside their card
const ROW_HEIGHT = 460

function pct(rate) {
  return rate === null || rate === undefined ? '—' : `${Math.round(rate * 100)}%`
}

function Tile({ label, value, sublabel }) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="h5">{value}</Typography>
        {sublabel && <Typography variant="caption" color="text.secondary">{sublabel}</Typography>}
      </CardContent>
    </Card>
  )
}

export default function DisputeReviewSection({ days = 90 }) {
  const [summary, setSummary] = useState(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    disputeReviewAPI.getSummary(days).then(setSummary).catch(() => setFailed(true))
  }, [days])

  if (failed) return <Alert severity="warning">Failed to load the AI review summary.</Alert>
  if (!summary) return null

  const attempted = Object.values(summary.statuses || {}).reduce((a, b) => a + b, 0)
  const health = summary.health || {}

  return (
    <Box data-testid="ai-review-section">
      <Typography variant="h6">AI review of finished tickets</Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Each finished dispute ticket is reviewed daily against the prompts its agents actually ran with.
      </Typography>

      {attempted === 0 ? (
        <Alert severity="info">No reviews yet. The dispute_review job reviews finished tickets each morning.</Alert>
      ) : (
        <>
          {(summary.health_warnings || []).map((w) => (
            <Alert key={w} severity="warning" sx={{ mb: 1 }}>{w}</Alert>
          ))}

          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Tile label="Tickets reviewed" value={summary.tickets_reviewed}
                sublabel={attempted > summary.tickets_reviewed ? `${attempted - summary.tickets_reviewed} rule checks only` : null} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Tile label="Clean" value={pct(health.clean_rate)} sublabel={`${summary.clean} with no problems`} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Tile label="High-severity findings" value={summary.high_findings} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Tile
                label="Reviewer health"
                value={summary.health_warnings?.length ? `${summary.health_warnings.length} warning(s)` : 'OK'}
                sublabel={`${pct(health.drop_rate)} findings dropped · ${pct(health.invalid_output_rate)} invalid · ${pct(health.agreement_rate)} agreed`}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} md={7}>
              <Box sx={{ height: { md: ROW_HEIGHT } }}>
                <Panel title="Most common problems">
                  {summary.top_issues?.length ? (
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Problem</TableCell>
                          <TableCell align="right">Count</TableCell>
                          <TableCell>Mostly in</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {summary.top_issues.map((issue) => (
                          <TableRow key={issue.key}>
                            <TableCell>
                              {issue.label}{' '}
                              <Chip size="small" variant="outlined" label={issue.source === 'rule' ? 'Rule' : 'AI'} />
                            </TableCell>
                            <TableCell align="right">{issue.count}</TableCell>
                            <TableCell>{issue.mostly_in}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <Typography variant="body2" color="text.secondary">No problems found.</Typography>
                  )}
                  {Object.keys(summary.faults || {}).length > 0 && (
                    <Box mt={1.5} display="flex" gap={1} flexWrap="wrap" alignItems="center">
                      <Typography variant="caption" color="text.secondary">Whose fault:</Typography>
                      {Object.entries(summary.faults).map(([fault, count]) => (
                        <Chip key={fault} size="small" label={`${FAULT_LABELS[fault] || fault}: ${count}`} />
                      ))}
                    </Box>
                  )}
                </Panel>
              </Box>
            </Grid>

            <Grid item xs={12} md={5}>
              <Box display="flex" flexDirection="column" gap={2} sx={{ height: { md: ROW_HEIGHT } }}>
                {/* The ticket list gets the larger share; the version table can't squeeze it */}
                <Box sx={{ flex: { md: '3 1 0' }, minHeight: { md: 180 } }}>
                  <Panel title="Tickets with high-severity problems">
                    {summary.high_tickets?.length ? (
                      summary.high_tickets.map((t) => (
                        <Box key={t.ticket_id} py={0.5}>
                          <Link component={RouterLink} to={`/tickets/${t.ticket_id}`}>#{t.ticket_id}</Link>{' '}
                          <Typography component="span" variant="body2" color="text.secondary">
                            {t.summary || `${t.high_count} high-severity finding(s)`}
                          </Typography>
                        </Box>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">None.</Typography>
                    )}
                  </Panel>
                </Box>
                {summary.by_prompt_version?.length > 1 && (
                  <Box sx={{ flex: { md: '2 1 0' }, minHeight: { md: 0 } }}>
                  <Panel title="By orchestrator prompt version">
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Version</TableCell>
                          <TableCell align="right">Tickets</TableCell>
                          <TableCell align="right">Findings / ticket</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {summary.by_prompt_version.map((v) => (
                          <TableRow key={v.version}>
                            <TableCell><code>{v.version}</code></TableCell>
                            <TableCell align="right">{v.tickets}</TableCell>
                            <TableCell align="right">{v.findings_per_ticket}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Panel>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  )
}
